/**
 * Currency Store
 *
 * Manages the user's preferred currency for price display.
 * Uses Zustand with persistence for cross-session consistency.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { formatCurrency } from '@/lib/utils';

export type Currency = 'EUR' | 'GBP';

export interface CurrencyInfo {
    code: Currency;
    symbol: string;
    name: string;
    locale: string;
}

export const CURRENCIES: Record<Currency, CurrencyInfo> = {
    EUR: {
        code: 'EUR',
        symbol: '€',
        name: 'Euro',
        locale: 'en-IE',
    },
    GBP: {
        code: 'GBP',
        symbol: '£',
        name: 'British Pound',
        locale: 'en-GB',
    },
};

interface CurrencyState {
    currency: Currency;
    setCurrency: (currency: Currency) => void;
    getCurrencyInfo: () => CurrencyInfo;
    formatPrice: (amount: number) => string;
}

export const useCurrencyStore = create<CurrencyState>()(
    persist(
        (set, get) => ({
            currency: 'EUR',
            setCurrency: (currency) => set({ currency }),
            getCurrencyInfo: () => CURRENCIES[get().currency],
            formatPrice: (amount) => {
                const info = CURRENCIES[get().currency];
                return formatCurrency(amount, info.code, info.locale);
            },
        }),
        {
            name: 'currency-storage',
        }
    )
);
